import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { getMyGroup, type MyGroupResult } from '@/lib/firebase/queries/myGroup';
import { onCompetitionLogChanged } from '@/lib/competitions/competitionLogEvents';

interface MyGroupCardProps {
  /** The active organization (dashboard switcher). */
  organizationId?: string;
  /** Rank by the organization's competition (off when competitions are hidden for this org). */
  withRanking?: boolean;
}

/**
 * «مجموعتي» (namat#36, mobile MyGroupCard / namat-mobile#17): the
 * beneficiary's group and section (or «بدون قسم»), their rank in the group,
 * the group's rank «X من N», and the members — names only. Ranks come from
 * the same competition standings as the competition card. Without a
 * competition it still shows the group and its members, unranked. Renders
 * nothing when the beneficiary has no group in the active organization.
 */
export function MyGroupCard({ organizationId, withRanking = true }: MyGroupCardProps) {
  const { currentUser } = useAuth();
  const { t, dir, language } = useLanguage();
  const [data, setData] = useState<MyGroupResult | null>(null);
  const [open, setOpen] = useState(false);
  const reqId = useRef(0);
  const uid = currentUser?.uid;
  const align = dir === 'rtl' ? 'text-right' : 'text-left';

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    if (!organizationId || !uid) {
      setData(null);
      return;
    }
    try {
      const result = await getMyGroup(organizationId, uid, language, { withRanking });
      if (mine === reqId.current) setData(result);
    } catch (error) {
      // Keep what we had: a failed refresh must not make the group disappear.
      console.error('Error loading my group:', error);
    }
  }, [organizationId, uid, language, withRanking]);

  useEffect(() => {
    setOpen(false);
    void load();
  }, [load]);

  // Ranks move with every competition log on this page, like the competition card.
  useEffect(() => (withRanking ? onCompetitionLogChanged(() => void load()) : undefined), [withRanking, load]);

  if (!data) return null;
  const { summary, names, competition } = data;
  const you = ` (${t('dashboard.competition.you')})`;
  const label = (key: string, values?: Record<string, string | number>) => t(`dashboard.competition.myGroup.${key}`, values);
  const rankOf = (rank: number, total: number) => label('rankOf', { rank, total });
  const membersId = `my-group-members-${summary.groupId}`;

  return (
    <Card className="overflow-hidden" dir={dir} data-testid="my-group-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#7dda55]/20 shrink-0">
            <Users className="h-5 w-5 text-[#7dda55]" aria-hidden />
          </div>
          <div className={cn('flex-1 min-w-0', align)}>
            <h3 className="text-xs font-semibold text-muted-foreground">{label('title')}</h3>
            <div className="font-bold truncate" data-testid="my-group-name">{summary.groupName}</div>
            <div className="text-xs text-muted-foreground truncate" data-testid="my-group-section">
              {summary.sectionName ? `${label('section')}: ${summary.sectionName}` : label('noSection')}
            </div>
          </div>
        </div>

        {competition ? (
          <>
            <div className="flex gap-2">
              <div className="flex-1 rounded-xl bg-[#7dda55]/10 py-2 text-center" data-testid="my-group-my-rank">
                <div className="text-base font-extrabold tabular-nums">
                  {summary.myRank ? rankOf(summary.myRank, summary.rankedCount) : '—'}
                </div>
                <div className="text-xs text-muted-foreground">{label('myRank')}</div>
              </div>
              <div className="flex-1 rounded-xl bg-[#7dda55]/10 py-2 text-center" data-testid="my-group-group-rank">
                <div className="text-base font-extrabold tabular-nums">
                  {summary.groupRank ? rankOf(summary.groupRank, summary.groupCount) : '—'}
                </div>
                <div className="text-xs text-muted-foreground">{label('groupRank')}</div>
              </div>
            </div>
            <p className={cn('text-xs text-muted-foreground truncate', align)} data-testid="my-group-ranked-by">
              {label('rankedBy', { name: competition.name })}
              {competition.hasEnded ? ` · ${t('dashboard.competition.ended')}` : ''}
            </p>
          </>
        ) : withRanking ? (
          <p className={cn('text-xs text-muted-foreground', align)} data-testid="my-group-no-competition">
            {label('noCompetition')}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={membersId}
          data-testid="my-group-toggle-members"
          className="w-full inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-sm font-bold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7dda55]"
        >
          {open ? label('hideMembers') : label('showMembers', { count: summary.members.length })}
          {open ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        </button>

        {open && (
          <ol id={membersId} className="space-y-0.5" data-testid="my-group-members">
            {summary.members.map((m) => (
              <li
                key={m.userId}
                data-testid={`my-group-member-${m.userId}`}
                className={cn('flex items-center gap-3 px-2 py-1.5 rounded-md text-sm', m.isMe && 'bg-[#7dda55]/15')}
              >
                <span className="w-6 text-center font-bold tabular-nums" aria-hidden={m.rank === null}>
                  {m.rank ?? '•'}
                </span>
                <span className={cn('flex-1 truncate', align, m.isMe ? 'font-bold' : 'font-medium')}>
                  {names.get(m.userId) || t('dashboard.manager.competitions.insights.unknown')}
                  {m.isMe && you}
                </span>
                {m.completed !== null && <span className="font-bold text-[#7dda55] tabular-nums">{m.completed}</span>}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
